import { PageInfo } from "@/components/Pagination";
import { TypedDocumentNode, gql } from "@apollo/client";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";
import { OrderStage, PaymentMethod, PaymentStatus, OrderPaymentStatus } from "../types";
import type { CRMNote, Person } from "@/types";

export interface PaymentNode {
  id: string;
  type: PaymentMethod;
  balance: number;
  referenceNumber: string;
  updatedAt: string;
  description: string;
  person: Person;
  lineItems: { id: string }[];
  payer: {
    id: string;
    name: string;
  };
  amountCents: number;
  refundedAmountCents: number;
  status: PaymentStatus;
  notes?: CRMNote[];
  orderPayments: {
    id: string;
    amountCents: number;
    ledgerEntries: { amountCents: number }[];
    order: {
      id: string;
      totalPaid: number;
      totalRefunded: number;
      description: string;
      lineItems: { id: string }[];
      addons: { productVariantLookupKey: string }[];
      status: OrderStage;
      person: Person;
      paymentStatus: OrderPaymentStatus;
    };
  }[];
}

type Query = {
  commercePayments: {
    totalCount: number;
    nodes: PaymentNode[];
    pageInfo: PageInfo;
  };
};

export const PAYMENTS_QUERY: TypedDocumentNode<Query> = gql`
  query GetCommercePayments(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $query: String
    $type: [String!]
    $status: [String!]
  ) {
    commercePayments(
      first: $first
      last: $last
      after: $after
      before: $before
      filters: { search: $query, type: $type, status: $status }
    ) {
      nodes {
        id
        amountCents
        referenceNumber
        paidAt
        refundedAmountCents
        status
        type
        source
        updatedAt
        payer {
          id
          name
        }
        notes {
          ...CRMNoteFields
        }
        orderPayments {
          id
          amountCents
          ledgerEntries {
            amountCents
          }
          order {
            id
            totalPaid
            totalRefunded
            description
            lineItems {
              id
            }
            addons {
              productVariantLookupKey
            }
            status
            paymentStatus
            person {
              id
              firstName
              lastName
              profilePhoto
              pgaId
              mainProgramType {
                id
                name
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }

  ${CRM_NOTE_FIELDS_FRAGMENT}
`;
